import React, { useState, useEffect } from 'react';
import {
    Card, Input, Typography, Button, Modal,
    Row, Col, Form, message, Popconfirm
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { restApi } from '../../../constants/api';

const { Title } = Typography;
const { TextArea } = Input;

// Styles
const depsty: React.CSSProperties = {
    backgroundColor: 'rgb(154, 229, 220)',
    padding: '8px',
    fontSize: '12px',
    position: 'absolute',
    color: 'rgb(144, 98, 0)',
    height: 'auto',
    fontFamily: 'system-ui',
    top: '-17px',
    borderRadius: '42px',
    zIndex: 999,
    right: '-12px',
};

const maindiv: React.CSSProperties = {
    padding: '40px',
    borderRadius: '21px',
    marginTop: '19px',
    background: '#f5f7fa',
    minHeight: '100vh',
    position: 'relative',
};

const cardMain: React.CSSProperties = {
    borderRadius: 50,
    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
    padding: 0,
    background: '#fff',
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 0,
    position: 'relative',
};

// Type for note object
interface Note {
    id: string;
    title: string;
    description: string;
    department_name?: string;
    user_name?: string;
}

const NoteCard: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cardData, setCardData] = useState<Note[]>([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // Role Set 
    const rawRole = localStorage.getItem('role');
    const role = rawRole ? rawRole.trim().toUpperCase() : null;
    // console.log("Normalized Role:", role);

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        // setLoading(true);
        try {
            const res: any = await restApi('/api/notes/getAllNotes', 'GET');
            // console.log('API response:', res.data.notes);
            setCardData(res?.data.notes || []);
        } catch (err) {
            console.error(err);
            message.error('Failed to fetch notes');
        }
    };

    const onFinish = async (values: any) => {
        const payload = {
            title: values.title,
            description: values.description,
            // user_id:"", 
            // department_id: ""
        };

        try {
            const res: any = await restApi('/api/notes/create', 'POST', payload);
            // console.log("Create note response:", res);
            window.location.reload();

            if (res.data) {
                setCardData(prev => [res.data, ...prev]);
            }

            message.success('Note created successfully!');
            form.resetFields();
            setIsModalOpen(false);
        } catch (error) {
            message.error('Failed to create note');
        }
    };


    const onFinishFailed = () => {
        message.error('Please enter all required fields.');
    };

    const handleDelete = async (noteId: any) => {
        try {
            // console.log('Deleting note ID:', noteId);

            const res: any = await restApi(`/api/notes/deleted/${noteId}`, 'POST', {});
            // console.log('Delete response:', res);

            setCardData(prev => prev.filter(note => note.id !== noteId));
            message.success('Note deleted.');
        } catch (error: any) {
            // console.error('Delete error:', error);
            message.error('Failed to delete note');
        }
    };


    return (
        <div style={maindiv}>
            <div style={{ position: 'relative', margin: '0 auto' }}>
                <Title level={2} style={{ textAlign: 'left', marginBottom: 30 }}>
                    Notes
                </Title>
                {role !== 'VIEWER' && (
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<EditOutlined />}
                        size="large"
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}
                        onClick={() => setIsModalOpen(true)}
                    />
                )}
                <div style={{ padding: '40px', background: '#f0f2f5' }}>
                    <Row gutter={[24, 24]}>
                        {Array.isArray(cardData) && cardData.length > 0 ? (
                            cardData.map((item) => (
                                <Col key={item.id} xs={24} sm={24} md={24} lg={12} xl={8} xxl={8}>
                                    <div style={{ marginTop: "34px" }}>
                                        <Card style={cardMain} hoverable loading={loading}>
                                            {item.department_name && (
                                                <div style={depsty}>
                                                    <strong>{item.department_name}</strong>
                                                </div>
                                            )}
                                            <div style={{ marginBottom: 16, marginTop: 20 }}>
                                                <strong>{item.title}</strong>
                                            </div>
                                            <div style={{ marginBottom: 16 }}>
                                                {item.description}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                {role !== 'VIEWER' && (
                                                    <Popconfirm
                                                        title="Are you sure you want to delete this note?"
                                                        onConfirm={() => handleDelete(item.id)}
                                                        okText="Yes"
                                                        cancelText="No"
                                                    >
                                                        <Button danger size="small" icon={<DeleteOutlined />}>
                                                            Delete
                                                        </Button>
                                                    </Popconfirm>
                                                )}
                                                <span>- {item.user_name || 'Unknown'}</span>
                                            </div>
                                        </Card>
                                    </div>
                                </Col>
                            ))
                        ) : (
                            !loading && <p style={{ color: "red" }}>No notes found.</p>
                        )}
                    </Row>
                </div>

                <Modal
                    title="Create Note"
                    open={isModalOpen}
                    onCancel={() => {
                        form.resetFields();
                        setIsModalOpen(false);
                    }}
                    footer={null}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                    >
                        <Form.Item
                            label="Title"
                            name="title"
                            rules={[{ required: true, message: 'Please enter a title' }]}
                        >
                            <Input placeholder="Title" />
                        </Form.Item>

                        <Form.Item
                            label="Description"
                            name="description"
                            rules={[{ required: true, message: 'Please enter a description' }]}
                        >
                            <TextArea rows={3} placeholder="Description" />
                        </Form.Item>

                        <Form.Item style={{ textAlign: 'center' }}>
                            <Button type="primary" htmlType="submit">
                                Submit
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};

export default NoteCard;
